import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ArrowUpRight, ArrowLeft, Locate } from "lucide-react";
import { formatNumber } from "../utils/statsUtils";
import TopDaysChart from "./TopDays";
import TopSkiedRunsChart from "./TopSkiedRuns";

const Header = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  @media (max-width: 800px) {
    position: sticky;
    top: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 16px 0;
    margin: -16px -16px 16px -16px;
    padding-left: 16px;
    padding-right: 16px;
    z-index: 10;
    border-bottom: 1px solid #eee;
  }
`;

const ResortName = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #111;
`;

const Location = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: 2px;
`;

const StyledLocate = styled(Locate)`
  width: 16px;
  height: 16px;
  color: #999;
  margin-top: 4px;
  transition: color 0.2s ease;
`;

const StyledArrow = styled(ArrowUpRight)`
  width: 20px;
  height: 20px;
  color: #999;
  margin-top: 4px;
  transition: color 0.2s ease;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-top: 1px solid #eee;
  padding: 12px 4px;
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  margin: 2px 0;

  &:hover ${StyledLocate}, &:hover ${StyledArrow} {
    color: #005fcc;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #111;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #555;
  margin-top: 2px;
`;

const BackButton = styled(ArrowLeft)`
  color: #999;
  width: 18px;
  height: 18px;
  margin-bottom: 8px;
  cursor: pointer;
  &:hover {
    color: #005fcc;
  }
`;

export function useAnimatedNumber(targetNumber, duration = 800) {
  const [animatedNumber, setAnimatedNumber] = useState(
    formatNumber(targetNumber ?? 0)
  );
  const previousNumber = useRef(targetNumber ?? 0);

  useEffect(() => {
    let start;
    let frame;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);

      // Animate from previous number to target number
      const current =
        previousNumber.current +
        ((targetNumber ?? 0) - previousNumber.current) * progress;
      setAnimatedNumber(formatNumber(Math.round(current)));

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        previousNumber.current = targetNumber ?? 0;
      }
    };

    if (previousNumber.current !== (targetNumber ?? 0)) {
      frame = requestAnimationFrame(step);
    }

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [targetNumber, duration]);

  return animatedNumber;
}

export default function ResortDetail({
  resortMetaData,
  resortStats,
  onBack,
  onHoverLocation,
  resortRuns,
}) {
  const animatedTotalDistance = useAnimatedNumber(resortStats.totalDistance);
  const animatedTotalVertical = useAnimatedNumber(resortStats.totalVertical);
  const animatedTotalRuns = useAnimatedNumber(resortStats.totalRuns);
  const animatedTotalDays = useAnimatedNumber(resortStats.totalDays);
  const animatedLongestRun = useAnimatedNumber(resortStats.longestRun);
  const animatedSteepestSlope = useAnimatedNumber(resortStats.steepestSlope);
  const animatedCompletionPercentage = useAnimatedNumber(
    resortStats.completionPercentage
  );

  const [showTopDistance, setShowTopDistance] = useState(false);
  const [showTopVertical, setShowTopVertical] = useState(false);
  const [showTopRuns, setShowTopRuns] = useState(false);
  const [showTopSkiedRuns, setShowTopSkiedRuns] = useState(false);

  const handleSteepestSlopeHover = (isHovering) => {
    if (isHovering && resortStats.steepestSlopeLocation) {
      const [lng, lat] = resortStats.steepestSlopeLocation.coordinates;
      onHoverLocation({ lng, lat });
    } else {
      onHoverLocation(null);
    }
  };

  const handleLongestRunHover = (isHovering) => {
    if (isHovering && resortStats.longestRunLocation) {
      const [lng, lat] = resortStats.longestRunLocation.coordinates;
      onHoverLocation({
        lng,
        lat,
        segments: resortStats.longestRunLocation.segments,
      });
    } else {
      onHoverLocation(null);
    }
  };

  return (
    <>
      <Header>
        <BackButton
          onClick={() => {
            if (showTopRuns) {
              setShowTopRuns(false);
            } else if (showTopDistance) {
              setShowTopDistance(false);
            } else if (showTopVertical) {
              setShowTopVertical(false);
            } else if (showTopSkiedRuns) {
              setShowTopSkiedRuns(false);
            } else {
              onBack?.();
            }
          }}
        />
        <ResortName>{resortMetaData?.name}</ResortName>
        <Location>{resortMetaData?.location}</Location>
      </Header>
      <>
        {!showTopRuns &&
          !showTopVertical &&
          !showTopDistance &&
          !showTopSkiedRuns && (
            <>
              <StatRow $isClickable={false}>
                <StatInfo>
                  <StatValue>{animatedTotalDays}</StatValue>
                  <StatLabel>visits</StatLabel>
                </StatInfo>
              </StatRow>
              <StatRow
                onClick={() => {
                  setShowTopRuns(true);
                }}
                $isClickable={true}
              >
                <StatInfo>
                  <StatValue>{animatedTotalRuns}</StatValue>
                  <StatLabel>runs</StatLabel>
                </StatInfo>
                <StyledArrow />
              </StatRow>
              <StatRow
                onClick={() => {
                  setShowTopDistance(true);
                }}
                $isClickable={true}
              >
                <StatInfo>
                  <StatValue>{animatedTotalDistance}</StatValue>
                  <StatLabel>km</StatLabel>
                </StatInfo>
                <StyledArrow />
              </StatRow>
              <StatRow
                onClick={() => {
                  setShowTopVertical(true);
                }}
                $isClickable={true}
              >
                <StatInfo>
                  <StatValue>{animatedTotalVertical}</StatValue>
                  <StatLabel>vertical meter</StatLabel>
                </StatInfo>
                <StyledArrow />
              </StatRow>
              <StatRow
                $isClickable={false}
                onMouseEnter={() => handleLongestRunHover(true)}
                onMouseLeave={() => handleLongestRunHover(false)}
              >
                <StatInfo>
                  <StatValue>{animatedLongestRun}</StatValue>
                  <StatLabel>longest run (m)</StatLabel>
                </StatInfo>
                <StyledLocate />
              </StatRow>
              <StatRow
                $isClickable={false}
                onMouseEnter={() => handleSteepestSlopeHover(true)}
                onMouseLeave={() => handleSteepestSlopeHover(false)}
              >
                <StatInfo>
                  <StatValue>{animatedSteepestSlope}</StatValue>
                  <StatLabel>steepest slope (°)</StatLabel>
                </StatInfo>
                <StyledLocate />
              </StatRow>
              <StatRow
                $isClickable={true}
                onClick={() => setShowTopSkiedRuns(true)}
              >
                <StatInfo>
                  <StatValue>{animatedCompletionPercentage}%</StatValue>
                  <StatLabel>runs completed</StatLabel>
                </StatInfo>
                <StyledArrow />
              </StatRow>
            </>
          )}
        {showTopDistance && (
          <TopDaysChart data={resortStats.distancePerDay} unit="km" />
        )}
        {showTopVertical && (
          <TopDaysChart data={resortStats.verticalPerDay} unit="" />
        )}
        {showTopRuns && (
          <TopDaysChart data={resortStats.runsPerDay} unit="runs" />
        )}
        {showTopSkiedRuns && (
          <TopSkiedRunsChart
            resortRuns={resortRuns}
            onHoverLocation={onHoverLocation}
          />
        )}
      </>
    </>
  );
}
